import React, { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaGoogle, FaTelegramPlane, FaPhone } from "react-icons/fa";
import { useForm } from "react-hook-form";
import { loginSchema, signUpSchema } from "../services/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { postApi } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL, BASE_URL } from "../utils/constants";

const Auth = () => {
	const [isSignUp, setIsSignUp] = useState(false);
	const { login, user } = useContext(AuthContext);

	const navigate = useNavigate();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		resolver: zodResolver(isSignUp ? signUpSchema : loginSchema),
	});
	const redirect = (path) => {
		window.location.replace(`${BASE_URL}/${path}`);
	};
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const accessToken = params.get("accessToken");
		const success = params.get("success");
		if (accessToken && success) {
			login({ accessToken });
			switch (user?.role) {
				case "student":
					redirect("enroll");
					break;
				case "admin":
					redirect("admin");
					break;
				case "teacher":
					redirect("teachersProfile");
					break;
				default:
					redirect("");
			}
		}
	}, []);

	const containerVariants = {
		hidden: { opacity: 0, y: 50, scale: 0.8 },
		visible: {
			opacity: 1,
			y: 0,
			scale: 1,
			transition: { duration: 0.6, ease: "easeOut" },
		},
	};

	const formVariants = {
		hidden: { opacity: 0, rotateY: -90 },
		visible: {
			opacity: 1,
			rotateY: 0,
			transition: { duration: 0.8, ease: "easeOut" },
		},
	};
	const onSubmit = async (data) => {
		try {
			const res = isSignUp
				? await postApi("/users/registerStudent", data)
				: await postApi("/auth/login", data);

			if (res.error) {
				// Show error toast if the server returns an error
				toast.error(res.message || "An error occurred");
				return;
			} else {
				// Navigate to enroll if the user is signing up
				if (isSignUp) {
					toast.success(
						res.message ||
							"We have accepted your request now please login!!"
					);
					navigate("/enroll");
					return; // Prevent further navigation after signup
				} else {
					toast.success(res.message || "Welcome!!");
				}
			}
			login(res);

			switch (res?.user?.role) {
				case "student":
					navigate("/enroll");
					break;
				case "admin":
					navigate("/admin");
					break;
				case "teacher":
					navigate("/teachersProfile");
					break;
				default:
					navigate("/");
			}
		} catch (error) {
			// Show error toast for network or unexpected errors
			toast.error(error.response?.message || "Something went wrong");
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-white">
			<motion.div
				variants={containerVariants}
				initial="hidden"
				animate="visible"
				whileHover="hover"
				className="w-full max-w-lg p-8 bg-white backdrop-blur-lg rounded-xl transform transition-transform perspective"
			>
				<h1
					onClick={() => navigate("/")}
					className="ext-xl font-medium text-center mb-10 text-white bg-black border border-gray-800 rounded-full py-3 cursor-pointer"
				>
					Academy-nest
				</h1>
				<h1 className="text-xl font-semibold text-black text-center mt-2">
					{isSignUp ? "Create Account" : "Welcome Back"}
				</h1>
				<motion.form
					variants={formVariants}
					key={isSignUp ? "signUpForm" : "signInForm"}
					initial="hidden"
					animate="visible"
					className="mt-6 space-y-6"
					onSubmit={handleSubmit(onSubmit)}
				>
					<div className="space-y-4">
						{isSignUp && (
							<div>
								<label className="block text-gray-700">
									Full Name
								</label>
								<input
									type="text"
									placeholder="John Doe"
									className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 bg-white/90"
									{...register("fullName")}
								/>
								{errors.fullName && (
									<p className="text-red-700 text-sm mt-2">
										{errors.fullName.message}
									</p>
								)}
							</div>
						)}
						<div>
							<label className="block text-gray-700">Email</label>
							<input
								type="email"
								placeholder="you@example.com"
								className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 bg-white/90"
								{...register("email")}
							/>
							{errors.email && (
								<p className="text-red-700 text-sm mt-2">
									{errors.email.message}
								</p>
							)}
						</div>
						<div>
							<label className="block text-gray-700">
								Password
							</label>
							<input
								type="password"
								placeholder="********"
								className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 bg-white/90"
								{...register("password")}
							/>
							{errors.password && (
								<p className="text-red-700 text-sm mt-2">
									{errors.password.message}
								</p>
							)}
						</div>
					</div>
					<button
						type="submit"
						className="w-full text-white py-3 bg-black rounded-xl"
					>
						{isSignUp ? "Sign Up" : "Sign In"}
					</button>
				</motion.form>

				<div className="mt-10 w-full">
					<div className="flex items-center justify-center mb-10 space-x-4">
						<div className="flex-1 bg-gray-600 h-px" />
						<p className="text-center text-gray-600 text-sm">Or</p>
						<div className="flex-1 bg-gray-600 h-px" />
					</div>

					<a href={`${API_URL}/auth/google`}>
						<button className="flex items-center justify-center bg-white text-black border border-gray-800 py-3 w-full rounded-xl space-x-3">
							<FaGoogle size={16} />{" "}
							<span className="text-base">
								Sign in with Google
							</span>
						</button>
					</a>
				</div>

				<div className="mt-6 text-center">
					<p className="text-gray-600 text-sm">
						{isSignUp
							? "Already have an account?"
							: "Don't have an account?"}{" "}
						<span
							onClick={() => setIsSignUp(!isSignUp)}
							className="text-indigo-600 font-medium cursor-pointer hover:underline"
						>
							{isSignUp ? "Sign In" : "Sign Up"}
						</span>
					</p>
				</div>
			</motion.div>
		</div>
	);
};

export default Auth;
